import { WORKSPACE_CONTROLS, type WorkspaceControls, type WorkspaceControlsSet } from '@arcnet/contracts';
import { Switch } from '../../components/ui/Form.js';

/**
 * What the agent workspace offers (0153), as one editor for its three levels:
 *  - the ACCOUNT: a switch per control (inherit = null);
 *  - a QUEUE, and an AGENT: per control, inherit what the level above says, or set it on or off.
 * The inherited answer is shown in the choice itself, so nobody has to go and look it up.
 */
const GROUPS = [...new Set(WORKSPACE_CONTROLS.map((c) => c.group))];

export function WorkspaceControlsEditor({ value, onChange, inherit, inheritLabel, idPrefix, disabled }: {
  value: WorkspaceControlsSet;
  onChange: (next: WorkspaceControlsSet) => void;
  /** What applies where this level sets nothing; null for the account itself. */
  inherit: WorkspaceControls | null;
  /** "Account default", "From their queues". */
  inheritLabel?: string;
  idPrefix: string;
  disabled?: boolean;
}) {
  const set = (key: keyof WorkspaceControls, v: boolean | null): void => {
    const next = { ...value };
    if (v === null) delete next[key]; else next[key] = v;
    onChange(next);
  };
  return (
    <div className="arc-wsc">
      {GROUPS.map((g) => (
        <div key={g} className="arc-wsc-group">
          <span className="arc-wsc-head">{g}</span>
          {WORKSPACE_CONTROLS.filter((c) => c.group === g).map((c) => {
            const id = `${idPrefix}-${c.key}`;
            const own = value[c.key];
            return (
              <div key={c.key} className="arc-wsc-row">
                <div className="arc-stack" style={{ gap: 1, minWidth: 0 }}>
                  <label htmlFor={id} className="arc-wsc-label">{c.label}</label>
                  <span className="arc-wsc-hint">{c.hint}</span>
                </div>
                {inherit === null ? (
                  <Switch id={id} checked={own ?? c.default} disabled={disabled ?? false} onChange={(v) => set(c.key, v)} />
                ) : (
                  <select id={id} className="arc-select arc-wsc-select" disabled={disabled}
                    value={own === undefined ? '' : own ? 'on' : 'off'}
                    onChange={(e) => set(c.key, e.target.value === '' ? null : e.target.value === 'on')}>
                    <option value="">{inheritLabel ?? 'Inherit'} ({inherit[c.key] ? 'on' : 'off'})</option>
                    <option value="on">On</option>
                    <option value="off">Off</option>
                  </select>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
